import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";

const ActiveFormTables = ({
  forms,
  loading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onDownload,
  onEdit,
  onDelete,
  deleteLoading,
  onSort,
  sortField,
  sortDirection,
  downloadLoading,
}) => {
  // Sortable header component
  const SortableHeader = ({ field, children }) => {
    const isActive = sortField === field;
    const isAsc = isActive && sortDirection === "asc";
    const isDesc = isActive && sortDirection === "desc";

    return (
      <TableCell
        sx={{
          cursor: "pointer",
          userSelect: "none",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
        onClick={() => onSort(field)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {children}
          {isActive && (
            <>
              {isAsc && <ArrowUpwardIcon fontSize="small" />}
              {isDesc && <ArrowDownwardIcon fontSize="small" />}
            </>
          )}
        </Box>
      </TableCell>
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <SortableHeader field="form_name">Form Name</SortableHeader>
              <SortableHeader field="county">County</SortableHeader>
              <SortableHeader field="city">City</SortableHeader>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : forms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No forms found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              forms.map((form) => (
                <TableRow key={form.id} hover>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {form.label || form.fileName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {form.county ? (
                      <Chip
                        label={form.county}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {form.city ? (
                      <Chip
                        label={form.city}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <Tooltip title="Edit Form">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(form.form_id)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download Template">
                        <IconButton
                          size="small"
                          onClick={() => onDownload(form.form_id)}
                          color="primary"
                          className={
                            downloadLoading === form.id ? "rotate" : ""
                          }
                        >
                          {downloadLoading === form.id ? (
                            <RefreshIcon />
                          ) : (
                            <DownloadIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                      {form.canDelete && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() =>
                              onDelete(form.id, form.fileName, form.blobName)
                            }
                            color="error"
                            className={
                              deleteLoading === form.id ? "rotate" : ""
                            }
                          >
                            {deleteLoading === form.id ? (
                              <RefreshIcon />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};

export default ActiveFormTables;
